import {
	type ProductSearchQuery,
	useGetAcCategoriesQuery,
	useGetCategoryListQuery,
	useProductSearchQuery,
} from '@pco/api/generated/graphql';
import type { ProductSearch } from '@pco/api/graphQLDefinitions/queries/productSearch/productSearch.types';
import { PATHS } from '@pco/constants/paths';
import type { CategoriesNavigationImplProps } from '@pco/modules/CategoriesNavigation/index.impl';
import type {
	Category,
	CategoryListItem,
	CMSContext,
	SortByOption,
	SortBySelection,
} from '@pco/modules/CategoriesNavigation/index.types';
import {
	createSearchQueryFilters,
	createSortByForSearchQuery,
	useTotalItems,
	useTransformedCategories,
} from '@pco/modules/CategoriesNavigation/utils/categoryNavigation';
import {
	FITMENT_STATUS_SORT_BY,
	SORT_OPTIONS,
} from '@pco/modules/CategoriesNavigation/utils/constants.impl';
import { usePopularCategoriesNavigation } from '@pco/modules/CategoriesNavigation/utils/usePopularCategoriesNavigation';
import { useSearchCategoryNavigation } from '@pco/modules/CategoriesNavigation/utils/useSearchNavigation';
import type {
	PartialParamVehicle,
	Vehicle,
} from '@pco/modules/VehicleSelection';
import { useConfig } from '@pco/molecules/Config';
import type {
	ProductCardLocation,
	ProductCardView,
} from '@pco/molecules/ProductCard/index.types';
import { useCurrentDealer } from '@pco/utils/dealership';
import { getProductAttributes } from '@pco/utils/product';
import { useEffect, useMemo } from 'react';

type CategoryNavigationType = Extract<
	ProductCardLocation,
	'clearance' | 'best-seller' | 'plp' | 'popular'
>;

const createProductSearchArguments = ({
	context,
	sortBySelection,
	selectedCategory,
	pageSize,
	pageNumber,
	searchTerm,
	selectedVehicle,
	paramVehicle,
}: {
	context: CategoriesNavigationImplProps['context'];
	selectedCategory: string;
	searchTerm: string;
	sortBySelection: SortBySelection;
	pageSize: number;
	pageNumber: number;
	selectedVehicle?: Vehicle;
	paramVehicle?: PartialParamVehicle;
}) => {
	const base = {
		phrase: '',
		filter: createSearchQueryFilters({
			selectedCategory,
			selectedVehicle,
			paramVehicle,
		}),
		sort: createSortByForSearchQuery({
			userSelectedSortBy: [sortBySelection],
			selectedVehicle,
		}),
		pageSize: pageSize,
		page: Number(pageNumber) || 1,
	};
	if (context === 'search') {
		return { ...base, phrase: searchTerm };
	}
	return base;
};
export const getProductCardPrice = (
	isNational: boolean,
	item?: ProductSearch.ProductSearchResultItem,
): ProductCardView['price'] => {
	if (isNational) {
		return {
			final: {
				amount: {
					currency: null,
					value: null,
				},
			},
			regular: {
				amount: item?.product?.price_range?.maximum_price?.regular_price,
			},
		};
	}
	return {
		final: {
			amount: item?.dealer?.price?.final?.amount,
		},
		regular: {
			amount: item?.dealer?.price?.regular?.amount,
		},
	};
};
export const productViewToProductCardView = (
	productView?: ProductSearch.ProductView | null,
	price?: ProductCardView['price'],
): ProductCardView => {
	if (productView?.__typename === 'ComplexProductView') {
		return {
			...productView,
			type: 'bundle' as const,
			attributes: getProductAttributes(productView.attributes),
			price,
		};
	}
	return {
		...productView,
		type: 'simple' as const,
		attributes: getProductAttributes(productView?.attributes || []),
		price,
	};
};
export const transformProductData = ({
	items,
	isNational,
}: {
	items?: ProductSearchQuery['productSearch']['items'];
	isNational: boolean;
}): ProductCardView[] => {
	return (
		items
			?.filter(Boolean)
			.map((item) => {
				const price = getProductCardPrice(isNational, item);
				return Object.assign(
					productViewToProductCardView(item?.productView, price),
					{ dealer: item?.dealer },
				);
			})
			.filter((item) => item !== undefined && item !== null) || []
	);
};

export const useCategoryNavigation = ({
	category,
	context,
	pageNumber,
	pageSize,
	sortBySelection,
	searchTerm,
	setPage,
	selectedVehicle,
	paramVehicle,
}: {
	category?: NonNullable<
		Pick<NonNullable<CategoryListItem>, 'url_key' | 'url_path'>
	>;
	context: CMSContext;
	pageSize: number;
	pageNumber: number;
	sortBySelection: SortBySelection;
	searchTerm: string;
	setPage: (pageNumber: number) => void;
	selectedVehicle?: Vehicle;
	paramVehicle?: PartialParamVehicle;
}) => {
	const hasSearchTerm = searchTerm !== '';
	const categoryUrlKey = category?.url_key || '';
	const { REACT_APP_PRODUCT_LISTING_PAGE: PLP_PATH = PATHS.PLP } = useConfig();
	const {
		isLoading: isPopularFetching,
		popularCategories,
		popularProductsData,
		isPopularCategory,
		popularType,
	} = usePopularCategoriesNavigation({
		categoryUrlKey,
		pageNumber,
		pageSize,
		sortBySelection,
		selectedVehicle,
		paramVehicle,
	});

	const { isNational } = useCurrentDealer();
	const { isFetching: isAcCategoriesFetching, data: categoryTreeData } =
		useGetAcCategoriesQuery(
			{ url_key: categoryUrlKey },
			{ enabled: !hasSearchTerm && !isPopularCategory },
		);
	const acCategoryItem = categoryTreeData?.acCategories?.items
		? categoryTreeData?.acCategories?.items[0]
		: undefined;
	const categoryLevels: { url_key: string }[] =
		acCategoryItem?.breadcrumbs
			?.filter((breadcrumb) => !!breadcrumb && breadcrumb.category_level === 2)
			.map((breadcrumb) => ({
				url_key: breadcrumb?.category_url_key ?? '',
			})) ?? [];
	const { isFetching: isCategoriesFetching, data: categoriesData } =
		useGetCategoryListQuery(
			{
				url_key: categoryLevels[0]?.url_key || categoryUrlKey,
			},
			{ enabled: !isAcCategoriesFetching && !hasSearchTerm },
		);
	const {
		isLoading: isSearchFetching,
		searchCategories,
		searchProductsData,
	} = useSearchCategoryNavigation({
		searchTerm,
		sortBySelection,
		pageNumber,
		pageSize,
		setPage,
		selectedVehicle,
		paramVehicle,
		enabled: hasSearchTerm,
	});
	const { isFetching: isProductsFetching, data: productsData } =
		useProductSearchQuery(
			createProductSearchArguments({
				context,
				selectedCategory: category?.url_path ?? '',
				searchTerm,
				sortBySelection,
				pageSize,
				pageNumber,
				selectedVehicle,
				paramVehicle,
			}),
			{
				gcTime: 0,
				staleTime: 0,
				placeholderData: (prev) => prev,
				enabled: !isCategoriesFetching && !hasSearchTerm && !isPopularCategory,
			},
		);

	const transformedCategories: Category[] | undefined =
		useTransformedCategories({
			searchCategories,
			isPopularFetching,
			isPopularCategory,
			popularCategories,
			hasSearchTerm,
			categoriesData,
			categoryUrlPath: category?.url_path ?? '',
			isCategoriesFetching,
			isSearchFetching,
			PLP_PATH,
		});

	const validProducts: ProductCardView[] = useMemo(() => {
		if (isPopularCategory)
			return transformProductData({
				items: popularProductsData?.productSearch.items,
				isNational,
			});
		if (hasSearchTerm)
			return transformProductData({
				items: searchProductsData?.productSearch.items,
				isNational,
			});
		return transformProductData({
			items: productsData?.productSearch.items,
			isNational,
		});
	}, [
		productsData,
		hasSearchTerm,
		searchProductsData,
		popularProductsData,
		isPopularCategory,
		isNational,
	]);

	const totalItems = useTotalItems({
		popularProductsCount: popularProductsData?.productSearch.total_count ?? 0,
		searchProductsCount: searchProductsData?.productSearch.total_count ?? 0,
		productCount: productsData?.productSearch.total_count ?? 0,
		hasSearchTerm,
		isPopularCategory,
	});

	const navigationType: CategoryNavigationType = isPopularCategory
		? popularType
		: 'plp';

	const sortByOptions: SortByOption[] = useMemo(() => {
		if (hasSearchTerm) {
			return (
				searchProductsData?.attributeMetadata?.sortable
					?.map((sortOption) => ({
						label: sortOption.label ?? '',
						value: sortOption.attribute,
					}))
					.filter(
						(option) =>
							option.value !== FITMENT_STATUS_SORT_BY.attribute &&
							option.value !== SORT_OPTIONS.POSITION,
					) || []
			);
		}
		return (
			productsData?.attributeMetadata?.sortable
				?.map((sortOption) => ({
					label: sortOption.label ?? '',
					value: sortOption.attribute,
				}))
				.filter(
					(option) =>
						option.value !== FITMENT_STATUS_SORT_BY.attribute &&
						option.value !== SORT_OPTIONS.RELEVANCE,
				) || []
		);
	}, [searchProductsData, productsData, hasSearchTerm]);

	useEffect(() => {
		if (validProducts) {
			requestAnimationFrame(() => {
				window.scrollTo(0, 0);
			});
		}
	}, [validProducts]);

	return {
		categories: hasSearchTerm ? searchCategories : transformedCategories,
		totalItems: totalItems || 0,
		items: validProducts,
		sortByOptions,
		isLoading: isCategoriesFetching || isProductsFetching || isSearchFetching,
		navigationType,
	};
};
